import { UUID } from "crypto";
import { baseApi } from "./baseApi";

import { Module, Role, RolePermission } from "@interfaces/roles";
import { PaginatedObject, ResponsePagination, Response } from "@interfaces/response";

const rolesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getRoles: build.query<Array<Role>, string>({
            query: (params) => ({
                url: `roles?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Role>) => {
                return response.data.data;
            },
        }),
        getRolesPaginated: build.query<PaginatedObject<Role>, string>({
            query: (params) => ({
                url: `roles?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Role>) => {
                return response.data;
            },
        }),
        postRole: build.mutation<Role, any>({
            query: (body) => ({
                url: "roles",
                method: "POST",
                body: body,
            }),
        }),
        patchRole: build.mutation<Role, { roleId: UUID; body: any }>({
            query: ({ roleId, body }) => ({
                url: `roles/${roleId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteRole: build.mutation<Role, UUID>({
            query: (roleId) => ({
                url: `roles/${roleId}`,
                method: "DELETE",
            }),
        }),
        getModulesByCode: build.query<Module | undefined, string>({
            query: (code) => ({
                url: `modules`,
                params: {
                    q: `code:${code}`,
                },
            }),
            transformResponse: (response: Response<Array<Array<Module>>>) => {
                // console.log(response.data.data);
                return response.data.at(0)?.at(0);
            },
        }),
        getModules: build.query<Array<Module>, void>({
            query: () => ({
                url: `modules`,
                params: { size: 200 },
            }),
            transformResponse: (response: { data: Array<Array<Module>> }) => {
                return response.data[0] ?? [];
            },
        }),
        getRolePermissions: build.query<Array<RolePermission>, string>({
            query: (rolId) => ({
                url: `roles/${rolId}/permissions`,
            }),
            transformResponse: (response: { data: Array<RolePermission> }) => {
                return response.data;
            },
        }),
        postRolePermission: build.mutation<RolePermission, { rolId: string; body: any }>({
            query: ({ rolId, body }) => ({
                url: `roles/${rolId}/permissions`,
                method: "POST",
                body,
            }),
        }),
        patchRolePermission: build.mutation<RolePermission, { rolId: string; permissionId: string; body: any }>({
            query: ({ rolId, permissionId, body }) => ({
                url: `roles/${rolId}/permissions/${permissionId}`,
                method: "PATCH",
                body,
            }),
        }),
        deleteRolePermission: build.mutation<void, { rolId: string; permissionId: string }>({
            query: ({ rolId, permissionId }) => ({
                url: `roles/${rolId}/permissions/${permissionId}`,
                method: "DELETE",
            }),
        }),
    }),
});

export const {
    useLazyGetRolesQuery,
    useGetRolesQuery,
    useLazyGetRolesPaginatedQuery,
    usePostRoleMutation,
    usePatchRoleMutation,
    useDeleteRoleMutation,
    useGetModulesByCodeQuery,
    useLazyGetModulesByCodeQuery,
    useGetModulesQuery,
    useGetRolePermissionsQuery,
    usePostRolePermissionMutation,
    usePatchRolePermissionMutation,
    useDeleteRolePermissionMutation,
} = rolesApi;
